// Service Firebase pour la Fan Zone EFAFC
import { db } from '../../../firebase';
import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  addDoc,
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs,
  serverTimestamp,
  increment
} from 'firebase/firestore';
import { calculateLevel, getTodayDateKey, GAMES_CONFIG } from './fanZoneConfig';
import logger from '../../../utils/logger';

// Collections Firebase
const COLLECTIONS = {
  USERS: 'fanzone_users',
  DAILY_CHALLENGES: 'fanzone_daily_challenges',
  SCORES: 'fanzone_scores'
};

// ==================== USER MANAGEMENT ====================

/**
 * Récupère ou crée le profil Fan Zone d'un utilisateur
 */
export const getUserProfile = async (userId) => {
  try {
    const userRef = doc(db, COLLECTIONS.USERS, userId);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      return { id: userSnap.id, ...userSnap.data() };
    }
    
    return null;
  } catch (error) {
    logger.error('Erreur getUserProfile:', error);
    throw error;
  }
};

/**
 * Crée un nouveau profil Fan Zone
 */
export const createUserProfile = async (userId, displayName) => {
  try {
    const userRef = doc(db, COLLECTIONS.USERS, userId);
    
    const newProfile = {
      pseudo: displayName || 'Fan EFAFC',
      avatar: '',
      totalXP: 0,
      level: 1,
      gamesPlayed: {
        pronostics: 0,
        quiEstCe: 0,
        wordle: 0,
        puzzle: 0
      },
      streaks: {
        pronosticsExacts: 0
      },
      createdAt: serverTimestamp(),
      lastPlayedAt: serverTimestamp()
    };
    
    await setDoc(userRef, newProfile);
    
    return { id: userId, ...newProfile };
  } catch (error) {
    logger.error('Erreur createUserProfile:', error);
    throw error;
  }
};

/**
 * Récupère ou crée le profil utilisateur
 */
export const getOrCreateUserProfile = async (userId, displayName) => {
  let profile = await getUserProfile(userId);
  
  if (!profile) {
    profile = await createUserProfile(userId, displayName);
  }
  
  return {
    ...profile,
    levelInfo: calculateLevel(profile.totalXP || 0)
  };
};

/**
 * Met à jour le pseudo de l'utilisateur
 */
export const updateUserPseudo = async (userId, newPseudo) => {
  try {
    const userRef = doc(db, COLLECTIONS.USERS, userId);
    await updateDoc(userRef, {
      pseudo: newPseudo
    });
    return true;
  } catch (error) {
    logger.error('Erreur updateUserPseudo:', error);
    throw error;
  }
};

// ==================== SCORE MANAGEMENT ====================

/**
 * Enregistre un score de jeu
 */
export const saveGameScore = async (userId, gameType, difficulty, points, details = {}) => {
  try {
    // 1. Ajouter le score à l'historique
    await addDoc(collection(db, COLLECTIONS.SCORES), {
      userId,
      gameType,
      difficulty,
      points,
      details,
      date: serverTimestamp(),
      dateKey: getTodayDateKey()
    });
    
    // 2. Mettre à jour le profil utilisateur
    const userRef = doc(db, COLLECTIONS.USERS, userId);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      const userData = userSnap.data();
      const newTotalXP = (userData.totalXP || 0) + points;
      const newLevel = calculateLevel(newTotalXP);
      
      await updateDoc(userRef, {
        totalXP: increment(points),
        level: newLevel.level,
        [`gamesPlayed.${gameType}`]: increment(1),
        lastPlayedAt: serverTimestamp()
      });
      
      return {
        pointsGained: points,
        newTotalXP,
        newLevel,
        levelUp: newLevel.level > (userData.level || 1)
      };
    }
    
    return { pointsGained: points };
  } catch (error) {
    logger.error('Erreur saveGameScore:', error);
    throw error;
  }
};

/**
 * Vérifie si l'utilisateur a déjà joué à un jeu aujourd'hui
 */
export const hasPlayedToday = async (userId, gameType, difficulty = null) => {
  try {
    const todayKey = getTodayDateKey();
    
    let q = query(
      collection(db, COLLECTIONS.SCORES),
      where('userId', '==', userId),
      where('gameType', '==', gameType),
      where('dateKey', '==', todayKey)
    );
    
    if (difficulty) {
      q = query(
        collection(db, COLLECTIONS.SCORES),
        where('userId', '==', userId),
        where('gameType', '==', gameType),
        where('difficulty', '==', difficulty),
        where('dateKey', '==', todayKey)
      );
    }
    
    const snapshot = await getDocs(q);
    return !snapshot.empty;
  } catch (error) {
    logger.error('Erreur hasPlayedToday:', error);
    return false;
  }
};

// ==================== DAILY CHALLENGES ====================

/**
 * Récupère le défi du jour pour un jeu
 */
export const getDailyChallenge = async (gameType) => {
  try {
    const todayKey = getTodayDateKey();
    const challengeRef = doc(db, COLLECTIONS.DAILY_CHALLENGES, `${todayKey}_${gameType}`);
    const challengeSnap = await getDoc(challengeRef);
    
    if (challengeSnap.exists()) {
      return { id: challengeSnap.id, ...challengeSnap.data() };
    }
    
    return null;
  } catch (error) {
    logger.error('Erreur getDailyChallenge:', error);
    return null;
  }
};

/**
 * Crée un défi quotidien
 */
export const createDailyChallenge = async (gameType, challengeData) => {
  try {
    const todayKey = getTodayDateKey();
    const challengeRef = doc(db, COLLECTIONS.DAILY_CHALLENGES, `${todayKey}_${gameType}`);
    
    await setDoc(challengeRef, {
      gameType,
      dateKey: todayKey,
      ...challengeData,
      createdAt: serverTimestamp()
    });
    
    return true;
  } catch (error) {
    logger.error('Erreur createDailyChallenge:', error);
    throw error;
  }
};

// ==================== LEADERBOARD ====================

/**
 * Récupère le classement
 */
export const getLeaderboard = async (timeframe = 'all', limitCount = 10) => {
  try {
    // Pour 'all', on récupère les meilleurs scores totaux
    const q = query(
      collection(db, COLLECTIONS.USERS),
      orderBy('totalXP', 'desc'),
      limit(limitCount)
    );
    
    const snapshot = await getDocs(q);
    const leaderboard = [];
    
    let rankIndex = 0;
    snapshot.forEach((docSnap) => {
      rankIndex++;
      const data = docSnap.data();
      leaderboard.push({
        rank: rankIndex,
        userId: docSnap.id,
        pseudo: data.pseudo || 'Fan EFAFC',
        totalXP: data.totalXP || 0,
        level: data.level || 1,
        levelInfo: calculateLevel(data.totalXP || 0),
        gamesPlayed: data.gamesPlayed || {}
      });
    });
    
    return leaderboard;
  } catch (error) {
    logger.error('Erreur getLeaderboard:', error);
    return [];
  }
};

/**
 * Récupère la position d'un utilisateur dans le classement
 */
export const getUserRank = async (userId) => {
  try {
    // Récupérer tous les utilisateurs ordonnés par XP
    const q = query(
      collection(db, COLLECTIONS.USERS),
      orderBy('totalXP', 'desc')
    );
    
    const snapshot = await getDocs(q);
    let rank = 0;
    let totalPlayers = 0;
    
    snapshot.forEach((doc, index) => {
      totalPlayers++;
      if (doc.id === userId) {
        rank = index + 1;
      }
    });
    
    return { rank, totalPlayers };
  } catch (error) {
    logger.error('Erreur getUserRank:', error);
    return { rank: 0, totalPlayers: 0 };
  }
};

// ==================== STATS ====================

/**
 * Récupère les statistiques globales de la Fan Zone
 */
export const getFanZoneStats = async () => {
  try {
    const usersSnapshot = await getDocs(collection(db, COLLECTIONS.USERS));
    
    let totalPlayers = 0;
    let totalGamesPlayed = 0;
    let totalXP = 0;
    
    usersSnapshot.forEach(doc => {
      const data = doc.data();
      totalPlayers++;
      totalXP += data.totalXP || 0;
      
      if (data.gamesPlayed) {
        Object.values(data.gamesPlayed).forEach(count => {
          totalGamesPlayed += count || 0;
        });
      }
    });
    
    return {
      totalPlayers,
      totalGamesPlayed,
      totalXP,
      averageXP: totalPlayers > 0 ? Math.round(totalXP / totalPlayers) : 0
    };
  } catch (error) {
    logger.error('Erreur getFanZoneStats:', error);
    return { totalPlayers: 0, totalGamesPlayed: 0, totalXP: 0, averageXP: 0 };
  }
};

export default {
  getUserProfile,
  createUserProfile,
  getOrCreateUserProfile,
  updateUserPseudo,
  saveGameScore,
  hasPlayedToday,
  getDailyChallenge,
  createDailyChallenge,
  getLeaderboard,
  getUserRank,
  getFanZoneStats
};
